import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaExternalLinkAlt,
  FaFolderOpen,
  FaMinus,
  FaPlus,
  FaTools,
} from "react-icons/fa";
import apiClientAdmin from "../../../services/apiClientAdmin";
import { getUserInfo } from "../../../services/authService-sighup";
import { getHeaders as getAssetHeaders } from "../../ApiHeaders/apiHeaders";
import { getHeaders as getEntitlementsHeaders } from "../../ApiHeaders/apiHeadersEntitlements";
import LoadingStyle from "../../Loading/Loading";
import { SplitDivider, useResizableSplit } from "../../SplitPane/ResizableSplit";
import AssetForm from "../Asset-Form/AssetForm";
import ErrorDialog from "../ErrorDialog/ErrorDialog";
import Dialog from "../FormDialog/Dialog";
import { createAsset, fetchAllAssets, resetAssetLookup } from "../api/assetAdminApi";
import {
  AllFoldersStatus,
  getAllFoldersStatus,
  getCollectionObjectIds,
  grantAllFolders,
} from "../api/entitlementsAdminApi";
import { formatEntitlementsError } from "../api/entitlementsMessages";
import {
  ChecklistAction,
  ChecklistStep,
  buildChecklist,
  resourceCountFromSettled,
} from "./onboardingChecklist";
import OnboardingAssetTable, { OnboardingAssetRow } from "./OnboardingAssetTable";
import { createdAssets, rememberCreatedAsset, wasCreatedHere } from "./createdAssets";

const ALL_ASSETS = "BETM-All-Assets";
const OUTAGE_ASSETS = "Outage-Assets";

interface AssetOnboardingProps {
  registerTabSwitchGuard?: (guard: () => boolean | Promise<boolean>) => void;
  canDelete?: boolean;
  onOpenResources?: () => void;
}

type AssetRow = OnboardingAssetRow;

interface Collection {
  collection_id: number;
  collection_name: string;
}

interface Loaded {
  inAllAssets: boolean;
  folders: AllFoldersStatus | null;
  resourceCount: number | null;
  inOutageGroup: boolean | null;
}

const entBase = () => process.env.REACT_APP_ENTITLEMENTS_API || "";

const findCollection = (list: Collection[], name: string) =>
  list.find(
    (c) => (c.collection_name || "").toLowerCase() === name.toLowerCase(),
  );

const STATUS_STYLE: Record<ChecklistStep["status"], string> = {
  done: "bg-green-100 text-green-700",
  missing: "bg-red-100 text-red-700",
  warn: "bg-amber-100 text-amber-700",
  choice: "bg-gray-100 text-gray-700",
};
const STATUS_TEXT: Record<ChecklistStep["status"], string> = {
  done: "Done",
  missing: "Missing",
  warn: "Check",
  choice: "Choice",
};

// The gray action button the admin forms use (UserForm's Refresh access).
const BUTTON =
  "flex items-center gap-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed";

// Missing steps Fix all repairs, in the order it runs them. Outage stays a manual
// choice and Resources a link to the Resource tab.
const FIXABLE: ChecklistAction[] = ["addToAllAssets", "grantFolders"];

const noop = () => {};

/** Backend message from an asset-metadata (axios) error, else the error text. */
const describeError = (err: any): string => {
  const body = err?.response?.data;
  const backend =
    body?.error?.error?.message ||
    body?.error?.message ||
    (typeof body?.detail === "string" ? body.detail : undefined);
  return backend || formatEntitlementsError(err);
};

const overlay = (
  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
    <LoadingStyle />
  </div>
);

const AssetOnboarding: React.FC<AssetOnboardingProps> = ({
  registerTabSwitchGuard,
  canDelete = false,
  onOpenResources,
}) => {
  const [assets, setAssets] = useState<AssetRow[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  // The asset list (left panel) and the checklist (right panel) load separately.
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ title: string; details: string } | null>(
    null,
  );
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Latest asset the user picked; responses for any other asset are dropped.
  const currentIdRef = useRef<number | null>(null);
  // True while a write or cache clear is in flight (blocks double clicks).
  const inFlightRef = useRef(false);
  // The New asset form is open in the right panel; createDirty: it has edits.
  const [creating, setCreating] = useState(false);
  const [createDirty, setCreateDirty] = useState(false);
  const [discardPrompt, setDiscardPrompt] = useState<((ok: boolean) => void) | null>(null);

  // Same rule as Asset Setup: leaving a form with unsaved edits asks first.
  const confirmDiscard = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        if (!(creating && createDirty)) return resolve(true);
        setDiscardPrompt(() => (ok: boolean) => {
          setDiscardPrompt(null);
          if (ok) {
            setCreating(false);
            setCreateDirty(false);
          }
          resolve(ok);
        });
      }),
    [creating, createDirty],
  );

  useEffect(() => {
    registerTabSwitchGuard?.(confirmDiscard);
  }, [registerTabSwitchGuard, confirmDiscard]);

  const fail = (title: string, err: unknown) =>
    setError({ title, details: formatEntitlementsError(err) });

  const fetchCollections = async (): Promise<Collection[]> => {
    const res = await fetch(`${entBase()}/collection?enabled_only=false`, {
      headers: await getEntitlementsHeaders(),
    });
    if (!res.ok) throw new Error("Failed to fetch collections");
    const json = await res.json();
    return json.response?.data || [];
  };

  const fetchAssets = useCallback(async () => {
    try {
      setAssetsLoading(true);
      const all: AssetRow[] = await fetchAllAssets();
      // Assets created here this page load stay listed (see createdAssets.ts).
      const kept = createdAssets().filter((c) => !all.some((a) => a.asset_id === c.asset_id));
      setAssets([...all, ...kept]);
    } catch (err) {
      fail("Failed to fetch assets", err);
    } finally {
      setAssetsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const loadChecklist = useCallback(async (assetId: number) => {
    const stale = () => currentIdRef.current !== assetId;
    setLoading(true);
    try {
      const collections = await fetchCollections();
      if (stale()) return;
      const allAssets = findCollection(collections, ALL_ASSETS);
      const outage = findCollection(collections, OUTAGE_ASSETS);
      if (!allAssets) {
        setError({
          title: "Collection not found",
          details: `The ${ALL_ASSETS} collection was not found, so visibility cannot be checked.`,
        });
      }
      // A stale cache makes /resources/{id} 403 for a brand-new asset; that
      // must not hide the rest of the checklist. An asset with no resources yet
      // answers 404, which counts as none (resourceCountFromSettled).
      const [allIds, outageIds, folders, resources] = await Promise.all([
        allAssets ? getCollectionObjectIds(allAssets.collection_id) : Promise.resolve([] as number[]),
        outage ? getCollectionObjectIds(outage.collection_id) : Promise.resolve([] as number[]),
        getAllFoldersStatus(assetId),
        Promise.allSettled([
          getAssetHeaders().then((headers) =>
            apiClientAdmin.get(`/resources/${assetId}`, { headers }),
          ),
        ]).then(([r]) => r),
      ]);
      if (stale()) return;
      setLoaded({
        inAllAssets: allIds.includes(assetId),
        folders,
        resourceCount: resourceCountFromSettled(resources),
        inOutageGroup: outage ? outageIds.includes(assetId) : null,
      });
    } catch (err) {
      if (stale()) return;
      setLoaded(null);
      fail("Failed to load the onboarding checklist", err);
    } finally {
      if (!stale()) setLoading(false);
    }
  }, []);

  const selectedAsset = useMemo(
    () => assets.find((a) => a.asset_id === selectedId) ?? null,
    [assets, selectedId],
  );

  const onSelect = (id: number | null) => {
    setMessage(null);
    if (id === null) {
      currentIdRef.current = null;
      setLoading(false);
      setSelectedId(null);
      setLoaded(null);
      return;
    }
    currentIdRef.current = id;
    setSelectedId(id);
    setLoaded(null);
    loadChecklist(id);
  };

  const mappingWrite = async (
    collectionName: string,
    add: boolean,
    assetId: number,
  ) => {
    const collections = await fetchCollections();
    const collection = findCollection(collections, collectionName);
    if (!collection) {
      setError({
        title: "Collection not found",
        details: `The ${collectionName} collection was not found.`,
      });
      return false;
    }
    const headers = await getEntitlementsHeaders();
    const user = getUserInfo();
    const who = user ? user.email || user.fullName || "Unknown User" : "Unknown User";
    const res = add
      ? await fetch(`${entBase()}/collection-mapping`, {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({
            collection_id: collection.collection_id,
            object_id: assetId,
            description: "",
            enabled: "Y",
            sys_created_by: who,
            sys_modified_by: who,
          }),
        })
      : await fetch(
          `${entBase()}/collection-mapping/collection/${collection.collection_id}/object/${assetId}`,
          { method: "DELETE", headers },
        );
    if (!res.ok) {
      throw new Error(
        `${add ? "Adding to" : "Removing from"} ${collectionName} failed (HTTP ${res.status})`,
      );
    }
    return true;
  };

  // Fix all's last step: re-reads the asset list (assets created here stay in it)
  // and the open checklist so the page shows what Fix all did. Nobody's cached
  // access changes; Refresh entitlements on the Entitlements tab applies it.
  const reloadAfterFix = async () => {
    resetAssetLookup();
    await fetchAssets();
    const assetId = currentIdRef.current;
    if (assetId !== null) await loadChecklist(assetId);
  };

  const runAction = async (action: ChecklistAction) => {
    if (action === "openResources") {
      onOpenResources?.();
      return;
    }
    if (inFlightRef.current) return;
    const assetId = currentIdRef.current;
    if (!canDelete || assetId === null) return;
    inFlightRef.current = true;
    setBusy(true);
    setLoading(true);
    try {
      let ok = true;
      if (action === "grantFolders") {
        await grantAllFolders(assetId);
      } else if (action === "addToAllAssets") {
        ok = await mappingWrite(ALL_ASSETS, true, assetId);
      } else if (action === "toggleOutage") {
        ok = await mappingWrite(
          OUTAGE_ASSETS,
          !loaded?.inOutageGroup,
          assetId,
        );
      }
      if (ok && currentIdRef.current === assetId) await loadChecklist(assetId);
    } catch (err) {
      fail("Action failed", err);
    } finally {
      setLoading(false);
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  const onRowSelect = async (a: AssetRow) => {
    if (creating) {
      if (!(await confirmDiscard())) return;
      setCreating(false);
      setCreateDirty(false);
    }
    onSelect(a.asset_id);
  };

  const startCreate = () => {
    if (!canDelete || inFlightRef.current) return;
    setMessage(null);
    setCreateDirty(false);
    setCreating(true);
  };

  // Back to whatever was showing before New asset (the selection is untouched).
  const cancelCreate = async () => {
    if (!(await confirmDiscard())) return;
    setCreating(false);
    setCreateDirty(false);
  };

  // AssetForm's Create: the same POST Asset Setup makes. A failure throws back
  // into AssetForm, which reports it through handleApiError and keeps the form.
  const handleCreateAsset = async (payload: any) => {
    const created = await createAsset(payload);
    resetAssetLookup();
    const id = Number(created?.asset_id) || null;
    setCreating(false);
    setCreateDirty(false);
    if (id === null) {
      setError({
        title: "Asset created without an id",
        details: `The service saved "${payload?.asset_name ?? "the asset"}" but did not return its id. It shows in this list once your entitlements refresh. An admin can click Refresh entitlements on the Entitlements tab, which can take up to 15 minutes. Otherwise the hourly refresh runs at :06. Then open this tab again.`,
      });
      await fetchAssets();
      return;
    }
    const row: AssetRow = {
      asset_id: id,
      asset_name: created.asset_name ?? payload?.asset_name ?? `#${id}`,
      generation_subtype_code: created.generation_subtype_code ?? payload?.generation_subtype_code,
      iso_code: created.iso_code ?? payload?.iso_code,
    };
    rememberCreatedAsset(row);
    setAssets((prev) => [...prev.filter((a) => a.asset_id !== id), row]);
    onSelect(id);
    setMessage(`Asset "${row.asset_name}" created.`);
  };

  const steps = buildChecklist({
    asset: selectedAsset,
    inAllAssets: loaded?.inAllAssets ?? false,
    folders: loaded?.folders ?? null,
    resourceCount: loaded ? loaded.resourceCount : 0,
    inOutageGroup: loaded?.inOutageGroup ?? null,
  });

  const buttonContent = (step: ChecklistStep): React.ReactNode => {
    const icon = "w-3.5 h-3.5";
    switch (step.action) {
      case "addToAllAssets":
        return <><FaPlus className={icon} />Add to BETM-All-Assets</>;
      case "grantFolders":
        return <><FaFolderOpen className={icon} />Grant folders</>;
      case "openResources":
        return <><FaExternalLinkAlt className={icon} />Open Resources</>;
      case "toggleOutage":
        return loaded?.inOutageGroup
          ? <><FaMinus className={icon} />Remove from Outage-Assets</>
          : <><FaPlus className={icon} />Add to Outage-Assets</>;
      default:
        return null;
    }
  };

  const fixable =
    loaded === null
      ? []
      : FIXABLE.filter((action) =>
          steps.some((s) => s.action === action && s.status === "missing"),
        );

  // Runs the missing fixes in order (BETM-All-Assets, then folders), then reloads
  // the list and checklist. Stops at the first error.
  const fixAll = async () => {
    if (inFlightRef.current) return;
    const assetId = currentIdRef.current;
    const plan = fixable;
    if (!canDelete || assetId === null || plan.length === 0) return;
    inFlightRef.current = true;
    setBusy(true);
    setLoading(true);
    let doing = "";
    try {
      for (const action of plan) {
        if (action === "addToAllAssets") {
          doing = `adding the asset to ${ALL_ASSETS}`;
          if (!(await mappingWrite(ALL_ASSETS, true, assetId))) return;
        } else if (action === "grantFolders") {
          doing = "granting the FileCenter folder permissions";
          await grantAllFolders(assetId);
        }
      }
      doing = "reloading the asset list and checklist";
      await reloadAfterFix();
      setMessage(
        "Fix all done. An admin can use Refresh entitlements on the Entitlements tab to apply it for everyone." +
          (wasCreatedHere(assetId)
            ? " After a page reload, this asset is listed only once Refresh entitlements has run."
            : ""),
      );
    } catch (err) {
      fail(`Fix all stopped while ${doing}`, err);
      // Show whatever did get done before the failure.
      if (currentIdRef.current === assetId) await loadChecklist(assetId);
    } finally {
      setLoading(false);
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  // Fix and toggle actions write data; opening a tab is allowed for everyone.
  const showButton = (step: ChecklistStep) =>
    !!step.action && (canDelete || step.action === "openResources");

  const showChecklist = loaded !== null || selectedId === null;
  const split = useResizableSplit("xl");

  const title = creating ? "New asset" : selectedAsset?.asset_name ?? "Select an asset";
  const subtitle = creating
    ? "Fill in the asset record and click Create. Its checklist opens once it is saved."
    : selectedAsset
      ? [selectedAsset.generation_subtype_code, selectedAsset.iso_code].filter(Boolean).join(" · ")
      : selectedId === null
        ? canDelete
          ? "Pick an asset on the left, or create one with New asset."
          : "Pick an asset on the left to see its onboarding checklist."
        : "";

  return (
    <div
      ref={split.containerRef}
      style={split.containerStyle}
      className={`flex flex-col xl:flex-row xl:items-stretch bg-gray-100 p-4 min-h-full gap-4 ${split.containerClassName}`}
    >
      {/* Left panel: exactly one child besides the loading overlay (AgGridTheme.css
          stretches every non-overlay child to the panel height). */}
      <div
        className={`admin-tab-panel flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white relative ${split.leftClassName}`}
      >
        {assetsLoading && overlay}
        <OnboardingAssetTable
          assets={assets}
          selectedId={selectedId}
          onRowSelect={onRowSelect}
        />
      </div>

      <SplitDivider {...split.dividerProps} />
      <div className="admin-tab-panel admin-tab-form-panel relative flex-grow flex flex-col lg:basis-1/4 xl:self-stretch bg-white rounded-xl shadow-md">
        {loading && overlay}
        <div
          className={`bg-white rounded-lg shadow-md font-poppins flex flex-col ${
            creating ? "px-8 pt-8" : "p-6"
          }`}
        >
          <div className={`flex justify-between items-start gap-4 ${creating ? "" : "mb-6"}`}>
            <div className="min-w-0">
              <h2 className="text-xl font-semibold text-gray-700 truncate">{title}</h2>
              {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
            </div>
            <div className="flex gap-3 shrink-0">
              {canDelete && !creating && (
                <button
                  type="button"
                  className={`${BUTTON} px-4 py-2`}
                  disabled={busy}
                  onClick={startCreate}
                >
                  <FaPlus className="w-4 h-4" />
                  New asset
                </button>
              )}
            </div>
          </div>
          {message && !creating && <p className="text-sm text-green-700 mb-4">{message}</p>}

          {!creating && selectedId !== null && showChecklist && (
            <div>
              <div className="flex justify-between items-center gap-4 mb-2">
                <label className="block text-gray-700 font-medium">Onboarding checklist</label>
                {canDelete && fixable.length > 0 && (
                  <button
                    type="button"
                    className={`${BUTTON} px-3 py-1.5 text-sm`}
                    title="Add to BETM-All-Assets and grant FileCenter folders where missing, then refresh your access"
                    disabled={busy}
                    onClick={fixAll}
                  >
                    <FaTools className="w-3.5 h-3.5" />
                    Fix all
                  </button>
                )}
              </div>
              <ul className="bg-white rounded-lg border divide-y">
                {steps.map((step) => (
                  <li key={step.id} className="flex items-center gap-4 px-4 py-3">
                    <span
                      className={`inline-flex justify-center shrink-0 w-20 px-2 py-1 rounded-full text-xs font-semibold ${STATUS_STYLE[step.status]}`}
                    >
                      {STATUS_TEXT[step.status]}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800">{step.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{step.help}</p>
                    </div>
                    {showButton(step) && (
                      <button
                        type="button"
                        className={`${BUTTON} px-3 py-1.5 text-sm shrink-0 whitespace-nowrap`}
                        disabled={busy}
                        onClick={() => runAction(step.action!)}
                      >
                        {buttonContent(step)}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {creating && (
          <AssetForm
            onSaveAsset={handleCreateAsset}
            onSaveChanges={noop}
            editIndex={null}
            isoData={[]}
            selectedAsset={null}
            setSelectedAsset={noop}
            handleApiError={(err, title) => setError({ title, details: describeError(err) })}
            onClearSelection={noop}
            onDirtyChange={setCreateDirty}
            onModeChange={noop}
            onDeleteAsset={noop}
            canDelete={canDelete}
            onCancelCreate={cancelCreate}
          />
        )}
      </div>

      {discardPrompt && (
        <Dialog
          title="Unsaved Changes"
          message="You have unsaved changes. Are you sure you want to discard them?"
          confirmButtonText="Yes"
          cancelButtonText="No"
          onConfirm={() => discardPrompt(true)}
          onCancel={() => discardPrompt(false)}
          onClose={() => discardPrompt(false)}
        />
      )}
      {error && (
        <ErrorDialog
          title={error.title}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}
    </div>
  );
};

export default AssetOnboarding;
